import { gql } from "graphql-request";
import { IMAGE_FRAGMENT, SEO_FRAGMENT } from "./fragments";

/**
 * Fetches shared global data across all pages.
 * Includes localized header labels and footer configuration details.
 */
export const GET_GLOBAL_DATA = gql`
  query rootQuery($locale: I18NLocaleCode) {
    headers(locale: $locale) {
      documentId
      item_name
      key
      phrase
      createdAt
      locale
    }
    footerDatasources(locale: $locale) {
      documentId
      item_name
      labels {
        id
        contact_us_heading
        mystays_hotel_header_text
        property_list_label
        copyright_label
        mystays_hotel_bottom_label
      }
      text_datasource {
        id
        text_datasource
      }
      phone_section {
        id
        phone_css_class
        phone_call_us_label
        phone_number
        timings
      }
      email_section {
        id
        email_css_class
        email_contact_us_label
        email_address
      }
      contact_us_form {
        id
        contact_form_css_class
        contact_form_label
        contact_us_link_label
      }
      bottom_section {
        id
        mystays_logo {
          previewUrl
          url
          size
        }
      }
      createdAt
      locale
    }
  }
`;

/**
 * Retrieves navigation data including menu items and icons.
 * Used for header navigation structure.
 */
export const NAVIGATION_DATASOURCES = gql`
  ${IMAGE_FRAGMENT}
  query navigationDatasources(
    $locale: I18NLocaleCode
    $status: PublicationStatus
  ) {
    mystay(locale: $locale, status: $status) {
      shared_datasource {
        item_name
        navigation_datasources {
          item_name
          display_menu_items
          menu_items {
            documentId
            item_name
            menu_title
            menu_link
            show
            button_link_after_loggin
            title_after_loggin
            icon {
              ...ImageUrlProperties
            }
            secondary_icon {
              ...ImageUrlProperties
            }
          }
          documentId
        }
      }
    }
  }
`;

//  Fetches global icon settings including header and footer logos.
export const ICON_SETTING = gql`
  ${IMAGE_FRAGMENT}
  query iconSetting($locale: I18NLocaleCode, $status: PublicationStatus) {
    mystay(locale: $locale, status: $status) {
      settings {
        icon_setting {
          global {
            logo_image {
              ...ImageUrlProperties
            }
            footer_logo_image {
              ...ImageUrlProperties
            }
          }
        }
      }
    }
  }
`;

// Retrieves general configuration settings such as contact numbers.
export const GLOBAL_SETTINGS = gql`
  query globalSettings {
    mystay {
      settings {
        global_settings {
          general_configurations {
            meeting_space_phone_number
          }
        }
      }
    }
  }
`;

// Fetches configuration for the newsletter/subscribe popup.
export const SUBSCRIBE_SETTINGS = gql`
  query subcribeSettings($locale: I18NLocaleCode, $status: PublicationStatus) {
    mystay(locale: $locale, status: $status) {
      settings {
        subcribe_settings {
          settings {
            header_text
            header_popup_id
          }
        }
      }
    }
  }
`;

/**
 * Retrieves GoToPass banner and placement visibility data.
 * Used for dynamic alert banners across specific pages.
 */
export const GOTO_PASS_HEADER = gql`
  query gotoPassData($locale: I18NLocaleCode, $status: PublicationStatus) {
    sharedDatasources(
      locale: $locale
      pagination: { limit: -1 }
      status: $status
    ) {
      shared_datasource_gotopass_particular_page {
        alert_banner {
          description
          external_link
          id
        }
        show_goto_pass_placements
      }
    }
  }
`;

// Fetches localized header text for the search box component.
export const HEADER_TEXT_SETTING = gql`
  query headerTextSetting($locale: I18NLocaleCode, $status: PublicationStatus) {
    headerTextSettings(locale: $locale, status: $status) {
      search_box {
        top_destination_label
        show_destination_label
        searchbox_placeholder
      }
    }
  }
`;

// Retrieves localized footer content and corporate details.
export const FOOTER_TEXT_SETTING = gql`
  query footerTextSetting($locale: I18NLocaleCode, $status: PublicationStatus) {
    mystay(locale: $locale, status: $status) {
      settings {
        footer_text_setting {
          configurations {
            coporate_outline
          }
        }
      }
    }
  }
`;

// Fetches popup content for GoToPass member details including image and description.
export const DETAIL_POPUP = gql`
  ${IMAGE_FRAGMENT}
  query DetailData($locale: I18NLocaleCode, $status: PublicationStatus) {
    sharedDatasources(
      locale: $locale
      pagination: { limit: -1 }
      status: $status
    ) {
      shared_datasource_gotopass_particular_page {
        member_details_popup {
          Image {
            ...ImageUrlProperties
          }
          title
          description
          disclaimer_description
          button_text
          external_link
          id
        }
      }
    }
  }
`;

// Note: pending will do it later fix performance query (hot-fix)

/**
 * Fetches Japan area data including hotels and attractions.
 * Optimized query (hot-fix) for better performance.
 */
export const JAPAN_AREA_HOTEL = gql`
  ${IMAGE_FRAGMENT}
  query GetAllAreaHotels(
    $locale: I18NLocaleCode
    $pagination: PaginationArg
    $status: PublicationStatus
    $filters: JapanAreaFiltersInput
    $hotelsFilters2: JapanHotelFiltersInput
  ) {
    japanAreas(
      status: $status
      locale: $locale
      pagination: $pagination
      filters: $filters
    ) {
      documentId
      name: item_name
      item_name
      area_name
      longitude
      latitude
      title
      map_zoom_ratio
      attractions(pagination: { limit: -1 }) {
        documentId
        item_name
        seo {
          page_description
          page_title
          page_represent_image {
            ...ImageUrlProperties
          }
        }
        properties {
          name
          latitude
          longitude
          overview_image {
            ...ImageUrlProperties
          }
          featured_image {
            ...ImageUrlProperties
          }
          heading
          sub_heading
          short_description
          description
          address
          google_place_id
          overview_text
          train_information
          website_link
          map_image {
            ...ImageUrlProperties
          }
        }
        datasources {
          relative_hotels_datasources {
            properties {
              hotels
            }
          }
          around_attractions_datasources {
            properties {
              around_attractions
            }
          }
        }
      }
      cover_image {
        ...ImageUrlProperties
      }
      hotels(pagination: { limit: -1 }, filters: $hotelsFilters2) {
        item_name
        documentId
        slug
        properties {
          latitude
          longitude
          brand
          hotel_name
          address
          review_score
          reviews
          prefecture
          base_price
        }
        seo {
          page_represent_image {
            ...ImageUrlProperties
          }
        }
        map_properties {
          zoom_value
        }
        amenities_and_services {
          hotel_benefits
          amenities_and_services_customs
        }
        external_ids {
          tripla_backend_code
          tripla_booking_code
        }
        hotel_images {
          documentId
        }
        datasources {
          documentId
        }
        redirect {
          external_url
        }
      }
    }
  }
`;

/**
 * Fetches Japan area data including hotels and attractions.
 * Optimized query (hot-fix) for better performance.
 */
export const LEAN_JAPAN_AREA_HOTEL = gql`
  query GetLeanAreaHotels(
    $locale: I18NLocaleCode
    $pagination: PaginationArg
    $status: PublicationStatus
    $filters: JapanAreaFiltersInput
  ) {
    japanAreas(
      status: $status
      locale: $locale
      pagination: $pagination
      filters: $filters
    ) {
      documentId
      name: item_name
      item_name
    }
  }
`;



// Fetches hotel image sets (desktop/mobile) for display in sliders.
export const JAPAN_AREA_HOTEL_IMAGES = gql`
  ${IMAGE_FRAGMENT}
  query japanHotelImages(
    $filters: JapanHotelImageFiltersInput
    $locale: I18NLocaleCode
    $pagination: PaginationArg
  ) {
    japanHotelImages(
      filters: $filters
      locale: $locale
      pagination: $pagination
    ) {
      documentId
      slider_desktop(pagination: { limit: 2 }) {
        image_url {
          ...ImageUrlProperties
        }
        documentId
      }
      slider_mobile(pagination: { limit: 2 }) {
        image_url {
          ...ImageUrlProperties
        }
        documentId
      }
      documentId
      item_name
      desktop(pagination: { limit: 3 }) {
        media {
          ...ImageUrlProperties
        }
      }
      mobile(pagination: { limit: 3 }) {
        media {
          ...ImageUrlProperties
        }
      }
    }
  }
`;

// Retrieves slider datasource metadata (picked image references).
export const JAPAN_AREA_HOTEL_DATA_SOURCES = gql`
  query SliderDatasources(
    $filters: JapanHotelDatasourceFiltersInput
    $locale: I18NLocaleCode
    $pagination: PaginationArg
  ) {
    japanHotelDatasources(
      filters: $filters
      locale: $locale
      pagination: $pagination
    ) {
      documentId
      slider_datasources {
        information {
          picked_images_desktop
          picked_images_mobile
        }
      }
    }
  }
`;

// Fetches brand icons and URLs for top, middle, and bottom brand lists.
export const GET_HOTEL_BRAND_ICON = gql`
  query hotelBrandIcons(
    $locale: I18NLocaleCode
    $documentId: ID!
    $status: PublicationStatus
    $middleDocId: ID!
  ) {
    homeBrand(documentId: $documentId, locale: $locale, status: $status) {
      top_list_datasources {
        home_brands_top_list_datasources_crm_top_list_datasource_top_details {
          item_name
          icon {
            cloudflare_url
            url
          }
          brand_url
        }
      }
      middle_list_datasources(filters: { documentId: { eq: $middleDocId } }) {
        item_name
        icon {
          cloudflare_url
          url
        }
        hotel_button_link
      }
      bottom_list_datasources {
        documentId
        brand_details {
          item_name
          icon {
            cloudflare_url
            url
          }
          brand_link
        }
      }
    }
  }
`;

// Retrieves hotel benefit data (amenities and services) displayed globally.
export const GET_HOTELS_BENEFITS = gql`
  query HotelBenefitList($locale: I18NLocaleCode) {
    contents(locale: $locale) {
      amenities_and_services {
        hotel_benefits(pagination: { limit: -1 }) {
          documentId
          item_name
          title
          display_name
        }
      }
    }
  }
`;

/**
 * Used for building search filters for hotels and cities in Japan.
 * Includes SEO data and selected hotels.
 */
export const SEARCH_BY_HOTEL_AND_CITY_DATASOURCE = gql`
  query HotelAndCityDatasources(
    $locale: I18NLocaleCode
    $status: PublicationStatus
  ) {
    japanAreas(locale: $locale, status: $status, pagination: { limit: -1 }) {
      item_name
      documentId
      area_name
      selected_hotels
      hotels(pagination: { limit: -1 }) {
        item_name
        documentId
      }
      slug_hotel
      seo {
        page_description
        page_keywords
        page_title
        robots
        no_index
      }
    }
  }
`;

// Retrieves SEO configuration for city-level pages.
export const GET_CITY_SEO = gql`
  ${IMAGE_FRAGMENT}
  query GetCitySeo(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $filters: JapanAreaFiltersInput
  ) {
    japanAreas(locale: $locale, status: $status, filters: $filters) {
      item_name
      documentId
      slug_hotel
      seo {
        id
        page_title
        page_description
        page_keywords
        page_represent_image {
          ...ImageUrlProperties
        }
        robots
        no_index
      }
    }
  }
`;

// Fetches localized label and icon data for GoToPass user info display.
export const QUERY_GOTOPASS_INFO_LABEL = gql`
  query GotoPassInfoLabel($locale: I18NLocaleCode, $status: PublicationStatus) {
    sharedDatasourceGotopassParticularPages(locale: $locale, status: $status) {
      user_info {
        id
        currently_label
        icon {
          url
          cloudflare_url
        }
        point_label
        point_icon {
          url
          cloudflare_url
        }
        goto_pass_label
        point_available_label
        ranking_label
        silver_rank_icon {
          cloudflare_url
          url
        }
        goto_pass_icon {
          cloudflare_url
          url
        }
        gold_rank_icon {
          cloudflare_url
          url
        }
        platinum_rank_icon {
          url
          cloudflare_url
        }
        button_link
      }
    }
  }
`;

// Configuration for serializing query params when fetching GraphQL data.
export const QUERY_STRING_OPTIONS = {
  encodeValuesOnly: true,
  arrayFormat: "repeat",
};

/**
 * Generates query configuration for retrieving Japan area data with nested relationships.
 * Used in server-side or static data fetching utilities.
 */
export const getJapanAreasQueryConfig = (locale: string) => ({
  locale,
  fields: ["item_name", "documentId"],
  populate: {
    attractions: {
      fields: ["item_name", "documentId", "display_name", "slug"],
      populate: {
        properties: {
          populate: {
            featured_image: {
              fields: ["cloudflare_url, caption, alternativeText"],
            },
            overview_image: {
              fields: ["cloudflare_url, caption, alternativeText"],
            },
          },
        },
      },
    },
    hotels: {
      fields: ["item_name", "documentId"],
      populate: {
        datasources: {
          populate: {
            relative_attraction_datasources: {
              populate: {
                informations: {
                  fields: ["attractions_list"],
                },
              },
            },
          },
        },
        external_ids: {
          fields: ["tripla_booking_code"],
        },
      },
    },
  },
  pagination: { limit: 100 },
});

/**
 * Fetches hotel data for all locales (Japanese, Korean, English, Simplified & Traditional Chinese).
 * Used for multilingual synchronization and SEO mapping.
 */
export const GET_HOTEL_ALL_LOCALE = gql`
  query japanHotels($status: PublicationStatus) {
    ja: japanHotels(pagination: { limit: -1 }, locale: "ja", status: $status) {
      documentId
      locale
      item_name
      display_name
      properties {
        hotel_name
      }
      external_ids {
        tripla_booking_code
        tripla_backend_code
      }
    }
    ko: japanHotels(pagination: { limit: -1 }, locale: "ko", status: $status) {
      documentId
      locale
      item_name
      display_name
      properties {
        hotel_name
      }
      external_ids {
        tripla_booking_code
        tripla_backend_code
      }
    }
    en: japanHotels(pagination: { limit: -1 }, locale: "en", status: $status) {
      documentId
      locale
      item_name
      display_name
      properties {
        hotel_name
      }
      external_ids {
        tripla_booking_code
        tripla_backend_code
      }
    }
    zh: japanHotels(pagination: { limit: -1 }, locale: "zh", status: $status) {
      documentId
      locale
      item_name
      display_name
      properties {
        hotel_name
      }
      external_ids {
        tripla_booking_code
        tripla_backend_code
      }
    }
    zhHantTW: japanHotels(
      pagination: { limit: -1 }
      locale: "zh-Hant-TW"
      status: $status
    ) {
      documentId
      locale
      item_name
      display_name
      properties {
        hotel_name
      }
      external_ids {
        tripla_booking_code
        tripla_backend_code
      }
    }
  }
`;

// Retrieves SEO metadata for base pages using the reusable SEO fragment.
export const GET_SEO_DATA_BASE_PAGE = gql`
  ${SEO_FRAGMENT}
  query getSeoDataBasePage(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $filters: BasePageFiltersInput
  ) {
    basePages(status: $status, locale: $locale, filters: $filters) {
      item_name
      seo {
        ...SEOProperties
      }
    }
  }
`;
